import { cloneDeep } from "lodash-es";
import { defaultLang, defaultSite } from "./getSite";

export type CollectionQuerys = {
  key: string | string[];
  value?: string | null;
  filter?:
    | Record<string, any>
    | {
        key: string;
        value: string;
      }[];
}[];

export function mergeData(
  data: any,
  fields: string[],
  lang: string,
  site: string,
  mergePublishData?: boolean,
  excludeLangFields?: string[],
  relationFields?: string[]
) {
  const langData = data.lang_data?.find((i: any) => i.lang === Number(lang));

  // console.log("langData",langData, Number(lang), defaultLang);
  let langObj =
    langData && Number(lang) !== defaultLang
      ? Object.fromEntries(
          Object.entries(langData).filter(([key]) => {
            // console.log("merge start");
            if (key === "id") {
              // console.log("end with id");
              return false;
            }
            if (excludeLangFields) {
              // console.log("end with excludeLangField");
              return !excludeLangFields.includes(key);
            }
            if (fields.includes("*")) {
              // console.log("end with *");
              return true;
            }

            // console.log("merge", key, fields.includes(key));
            return fields.includes(key);
          })
        )
      : {};

  // 如果不是默认语言，则根据relationFields从主数据中获取关联字段
  if (Number(lang) !== defaultLang) {
    if (relationFields) {
      relationFields.forEach((field) => {
        // 判断field是否在langObj中
        if (langObj[field]) {
          langObj[field] = data[field];
        }
      });
    }
  }

  const publishData = data.publish?.find(
    (i: any) => i.publish_id.site === Number(site)
  )?.publish_id;

  if (publishData && mergePublishData) {
    return Object.assign({}, data, langObj, { publish: publishData });
  }

  return Object.assign({}, data, langObj);
}

/** 获取带多语言、发布的 collection 数据 */
export async function fetchCollectionsData<T>(
  /** collection 名称 */
  name: string,
  options: {
    /** 站点 id */
    site?: string | null;
    /** 语言id */
    lang?: string | null;
    /** 字段数组 */
    fields: string[];
    /** 是否排除发布管理字段在响应中，减少响应数据体积，默认为 false */
    excludePublishFields?: boolean;
    /** 排除从多语言合并的字段，一般传入关系字段，因为关系字段都在主数据上 */
    excludeLangFields?: string[];
    /** 页码 */
    page: number;
    /** 每页数量 */
    limit: number;
    /** 是否查询发布, 默认 true */
    publish?: boolean;
    /** 是否合并发布内容, 默认 true */
    mergePublishData?: boolean;
    /** 当前获取非主语言数据时，需要从主数据上获取的关联字段 */
    relationFields?: string[];
    querys?: {
      key: string | string[];
      value?: string | null;
      filter?:
        | Record<string, any>
        | {
            key: string | string[];
            value: string;
          }[];
    }[];
    /** 排序 */
    sort?: string[] | string;

    /** 是否在多语音中搜索 */
    searchLangData?: boolean;
    onlyData?: boolean;
    onlyPagination?: boolean;
    tags?: string[];
  }
) {
  const {
    publish = true,
    mergePublishData = true,
    excludePublishFields = false,
  } = options;
  const url = new URL(`${process.env.NEXT_PUBLIC_DATASERVER}/items/${name}`);

  if (options.lang) {
    url.searchParams.append("filter[lang_is_main][_eq]", "true");
  }

  if (!excludePublishFields) {
    url.searchParams.append(
      "fields",
      "id,publish.publish_id.*,lang_data.lang," +
        options.fields
          .map((i) => {
            return [i, `lang_data.${i}`].join(",");
          })
          .join(",")
    );
  } else {
    url.searchParams.append("fields", options.fields.join(","));
  }

  if (options.sort) {
    if (Array.isArray(options.sort)) {
      options.sort.forEach((i, index) => {
        url.searchParams.append(`sort[${index}]`, i);
      });
    } else {
      url.searchParams.append(`sort`, options.sort);
    }
  }

  if (publish) {
    // _or 0 未开启定时发布
    url.searchParams.append(
      "filter[_or][0][_and][0][publish][publish_id][status][_eq]",
      "PUBLISH"
    );
    url.searchParams.append(
      "filter[_or][0][_and][1][publish][publish_id][enable][_eq]",
      "false"
    );
    url.searchParams.append(
      "filter[_or][0][_and][2][publish][publish_id][site][id][_eq]",
      options.site?.toString() ?? defaultSite.toString()
    );

    // _or 1 开启定时发布
    url.searchParams.append(
      "filter[_or][1][_and][0][publish][publish_id][status][_eq]",
      "PUBLISH"
    );
    url.searchParams.append(
      "filter[_or][1][_and][1][publish][publish_id][enable][_eq]",
      "true"
    );
    url.searchParams.append(
      "filter[_or][1][_and][2][publish][publish_id][publish_date][_lte]",
      "$NOW"
    );
    url.searchParams.append(
      "filter[_or][1][_and][3][publish][publish_id][site][id][_eq]",
      options.site?.toString() ?? defaultSite.toString()
    );
  }

  const querys = cloneDeep(options.querys ?? []);

  /**
   * 如果查找的不是默认语言
   * 如果搜索的key未包含在excludeLangFields中
   * （若key是个数组，则取第一项做判断）
   * 则从lang_data中去搜索
   */
  if (
    options.lang &&
    options.lang !== defaultLang.toString() &&
    options.searchLangData
  ) {
    for (let i = 0; i < querys.length; i++) {
      if (querys[i].key) {
        let keyString: string;
        if (Array.isArray(querys[i].key)) {
          keyString = querys[i].key[0];
        } else {
          keyString = querys[i].key as string;
        }
        if (!options.excludeLangFields?.includes(keyString)) {
          const langKeys = ["lang_data"];
          if (Array.isArray(querys[i].key)) {
            langKeys.push(...querys[i].key);
          } else {
            langKeys.push(keyString);
          }
          querys[i].key = langKeys;
        }
      }
    }
  }

  querys?.forEach((i, index) => {
    /** 单个 value */
    if (i.value) {
      if (Array.isArray(i.key)) {
        url.searchParams.append(
          `filter[_and][${index + (publish ? 3 : 0)}]${i.key
            .map((i) => `[${i}]`)
            .join("")}[_eq]`,
          i.value
        );
      } else {
        url.searchParams.append(
          `filter[_and][${index + (publish ? 3 : 0)}][${i.key}][_eq]`,
          i.value
        );
      }
    }

    /** 传入查询条件 */
    if (i.filter) {
      let filterKey = `filter[_and][${index + (publish ? 3 : 0)}][${i.key}]`;
      if (Array.isArray(i.key)) {
        filterKey = `filter[_and][${index + (publish ? 3 : 0)}]${i.key
          .map((i) => `[${i}]`)
          .join("")}`;
      }
      if (Array.isArray(i.filter)) {
        i.filter.forEach(
          (
            subFilter: {
              key: string | string[];
              value: string;
            },
            subFilterIndex
          ) => {
            if (Array.isArray(subFilter.key)) {
              const subFilterKey = subFilter.key.map((j) => `[${j}]`).join("");
              url.searchParams.append(
                filterKey + `[${subFilterIndex}]${subFilterKey}`,
                subFilter?.value
              );
            } else {
              url.searchParams.append(
                filterKey + `[${subFilterIndex}][${subFilter.key}]`,
                subFilter?.value
              );
            }
          }
        );
      } else if (i.filter instanceof Object) {
        Object.keys(i.filter).forEach((key) => {
          const filterValue = (i?.filter as Record<string, any>)?.[key];
          /** 当传入的值为数组时，query key中需要拼上下标 */
          if (Array.isArray(filterValue)) {
            for (let i = 0; i < filterValue.length; i++) {
              url.searchParams.append(
                filterKey + `[${key}][${i}]`,
                filterValue[i]
              );
            }
          } else {
            url.searchParams.append(filterKey + `[${key}]`, filterValue);
          }
        });
      }
    }
  });

  const aggregateUrl = new URL(url);

  url.searchParams.append("page", options.page.toString());
  url.searchParams.append("limit", options.limit.toString());

  // console.log(url, 'url');
  

  aggregateUrl.searchParams.append("aggregate[count]", "*");

  const res = options.onlyPagination
    ? {
        data: [],
      }
    : await fetch(url, {
        next: {
          tags: options.tags ?? [name],
          revalidate: 60 * 60,
        },
      }).then((res) => res.json());

  const count = options.onlyData
    ? {
        data: [{ count: 0 }],
      }
    : await fetch(aggregateUrl, {
        next: {
          tags: options.tags ?? [name],
          revalidate: 60 * 60,
        },
      }).then((res) => res.json());
  const totalCount = count?.data?.[0].count ?? 0;
  if (options.lang) {
    return {
      data: res?.data?.map((item: any) =>
        mergeData(
          item,
          options.fields,
          options.lang ?? defaultLang.toString(),
          options.site ?? defaultSite.toString(),
          mergePublishData,
          options.excludeLangFields,
          options.relationFields
        )
      ) as T[],
      count: totalCount,
    };
  }
  return {
    data: (res?.data || []) as T[],
    count: totalCount,
  };
}

/** 获取 collection 数据 */
export async function fetchCollectionsDataPure<T>(
  /** collection 名称 */
  name: string,
  options: {
    /** 站点 id */
    site?: string | null;
    publish?: boolean;
    /** 字段数组 */
    fields: string[];
    /** 页码 */
    page: number;
    /** 每页数量 */
    limit: number;
    querys?: CollectionQuerys;
    /** 排序 */
    sort?: string[] | string;
  },
  tag?: string
) {
  const url = new URL(`${process.env.NEXT_PUBLIC_DATASERVER}/items/${name}`);
  url.searchParams.append("fields", "id," + options.fields.join(","));

  if (options.sort) {
    if (Array.isArray(options.sort)) {
      options.sort.forEach((i, index) => {
        url.searchParams.append(`sort[${index}]`, i);
      });
    } else {
      url.searchParams.append(`sort`, options.sort);
    }
  }

  if (options.publish) {
    url.searchParams.append(
      "filter[_and][0][publish][publish_id][status][_eq]",
      "PUBLISH"
    );
    url.searchParams.append(
      "filter[_and][1][publish][publish_id][site][id][_eq]",
      options.site?.toString() ?? defaultSite.toString()
    );
  }

  options.querys?.forEach((i, index) => {
    /** 单个 value */
    if (i.value) {
      if (Array.isArray(i.key)) {
        url.searchParams.append(
          `filter[_and][${index + (options.publish ? 2 : 0)}]${i.key
            .map((i) => `[${i}]`)
            .join("")}[_eq]`,
          i.value
        );
      } else {
        url.searchParams.append(
          `filter[_and][${index + (options.publish ? 2 : 0)}][${i.key}][_eq]`,
          i.value
        );
      }
    }

    /** 传入查询条件 */
    if (i.filter) {
      let filterKey = `filter[_and][${index + (options.publish ? 2 : 0)}][${
        i.key
      }]`;
      if (Array.isArray(i.key)) {
        filterKey = `filter[_and][${index + (options.publish ? 2 : 0)}]${i.key
          .map((i) => `[${i}]`)
          .join("")}`;
      }
      if (Array.isArray(i.filter)) {
        i.filter.forEach(
          (
            subFilter: {
              key: string | string[];
              value: string;
            },
            subFilterIndex
          ) => {
            if (Array.isArray(subFilter.key)) {
              const subFilterKey = subFilter.key.map((j) => `[${j}]`).join("");
              url.searchParams.append(
                filterKey + `[${subFilterIndex}]${subFilterKey}`,
                subFilter?.value
              );
            } else {
              url.searchParams.append(
                filterKey + `[${subFilterIndex}][${subFilter.key}]`,
                subFilter?.value
              );
            }
          }
        );
      } else if (i.filter instanceof Object) {
        Object.keys(i.filter).forEach((key) => {
          const filterValue = (i?.filter as Record<string, any>)?.[key];
          /** 当传入的值为数组时，query key中需要拼上下标 */
          if (Array.isArray(filterValue)) {
            for (let i = 0; i < filterValue.length; i++) {
              url.searchParams.append(
                filterKey + `[${key}][${i}]`,
                filterValue[i]
              );
            }
          } else {
            url.searchParams.append(filterKey + `[${key}]`, filterValue);
          }
        });
      }
    }
  });

  const aggregateUrl = new URL(url);

  url.searchParams.append("page", options.page.toString());
  url.searchParams.append("limit", options.limit.toString());

  aggregateUrl.searchParams.append("aggregate[count]", "*");

  const res = await fetch(
    url,
    tag
      ? {
          next: {
            revalidate: 60,
            tags: [tag],
          },
        }
      : {
          next: {
            revalidate: 60,
          },
        }
  ).then((res) => res.json());
  const count = await fetch(
    aggregateUrl,

    tag
      ? {
          next: {
            revalidate: 60,
            tags: [tag],
          },
        }
      : {
          next: {
            revalidate: 60,
          },
        }
  ).then((res) => res.json());
  const totalCount = count?.data?.[0].count ?? 0;

  return {
    data: (res?.data || []) as T[],
    count: totalCount,
  };
}

/** 获取单个 带多语言、发布 的数据 */
export async function fetchCollectionData<T>(
  /** collection 名称 */
  name: string,
  options: {
    /** 站点 id */
    site?: string | null;
    /** 语言id */
    lang?: string | null;
    /** 字段数组 */
    fields: string[];
    /** 是否查询发布, 默认 true */
    publish?: boolean;
    /** 排除从多语言合并的字段，一般传入关系字段，因为关系字段都在主数据上 */
    excludeLangFields?: string[];

    /** 是否合并发布内容, 默认 true */
    mergePublishData?: boolean;
    /** 当前获取非主数据时，需要从主数据上获取的关联字段 */
    relationFields?: string[];
    /** next 缓存标识tag */
    tags?: string[];

    querys?: {
      key: string;
      value?: string | null;
    }[];
  } & (
    | {
        id?: string;
      }
    | { slug?: string }
  )
) {
  const querys = [];

  if ("id" in options) {
    querys.push({
      key: "id",
      value: options.id,
    });
  }

  if ("slug" in options) {
    querys.push({
      key: "slug",
      value: options.slug,
    });
  }

  const result = await fetchCollectionsData<T>(name, {
    ...options,
    querys,
    page: 1,
    limit: 1,
  });

  const data = result.data?.[0];
  if (data) {
    // console.log(data);
    return data;
  }
}

/** 获取单个的数据 */
export async function fetchCollectionDataPure<T>(
  /** collection 名称 */
  name: string,
  options: {
    /** 站点 id */
    site?: string | null;
    publish?: boolean;
    /** 字段数组 */
    fields: string[];
    querys?: {
      key: string;
      value?: string | null;
    }[];
  } & (
    | {
        id?: string;
      }
    | { slug?: string }
  ),
  tag?: string
) {
  const querys = [];

  if ("id" in options) {
    querys.push({
      key: "id",
      value: options.id,
    });
  }

  if ("slug" in options) {
    querys.push({
      key: "slug",
      value: options.slug,
    });
  }

  const result = await fetchCollectionsDataPure<T>(
    name,
    {
      ...options,
      querys,
      page: 1,
      limit: 1,
    },
    tag
  );

  const data = result.data?.[0];
  return data;
}

/** 获取草稿预览数据 */
export async function fetchDraftData<T>(
  name: string,
  options: {
    id: number | string;
    /** 字段数组 */
    fields: string[];
    querys?: {
      key: string;
      value?: string | null;
      filter?: Record<string, any>;
    }[];
  }
) {
  const url = new URL(
    `${process.env.NEXT_PUBLIC_DATASERVER}/items/${name}/${options.id}`
  );

  url.searchParams.append(
    "fields",
    "id,publish.publish_id.*,lang_data.lang," +
      options.fields
        .map((i) => {
          return [i, `lang_data.${i}`].join(",");
        })
        .join(",")
  );

  options.querys?.forEach((i, index) => {
    /** 单个 value */
    if (i.value) {
      if (Array.isArray(i.key)) {
        url.searchParams.append(
          `filter[_and][${index}]${i.key.map((i) => `[${i}]`).join("")}[_eq]`,
          i.value
        );
      } else {
        url.searchParams.append(
          `filter[_and][${index}][${i.key}][_eq]`,
          i.value
        );
      }
    }

    /** 传入查询条件 */
    if (i.filter) {
      let filterKey = `filter[_and][${index}][${i.key}]`;
      if (Array.isArray(i.key)) {
        filterKey = `filter[_and][${index}]${i.key
          .map((i) => `[${i}]`)
          .join("")}`;
      }
      Object.keys(i.filter).forEach((key) => {
        const filterValue = i?.filter?.[key];
        /** 当传入的值为数组时，query key中需要拼上下标 */
        if (Array.isArray(filterValue)) {
          for (let i = 0; i < filterValue.length; i++) {
            url.searchParams.append(
              filterKey + `[${key}][${i}]`,
              filterValue[i]
            );
          }
        } else {
          url.searchParams.append(filterKey + `[${key}]`, filterValue);
        }
      });
    }
  });

  const res = await fetch(url).then((res) => res.json());

  return res?.data as T | undefined;
}
